<script setup lang="ts">
import Icon from '@/components/partials/Icon.vue'
import { type Address, addressToLocation } from '@/http/models/Address'
import type { Location } from "@/types/Location"

defineProps<{
  addresses: Address[]
}>()

const emit = defineEmits<{
  (e: 'select', location: Location): void
}>()

const selectAddress = (address: Address): void => {
  emit('select', addressToLocation(address))
}
</script>

<template>
  <div class="recent-addresses" data-test-id="recent-addresses" v-if="addresses">
    <div v-for="address in addresses.slice(0, 2)"
         :key="address.id"
         data-test-id="recent-address"
         class="recent-addresses__address"
         @click="selectAddress(address)">
      <span class="recent-addresses__address-label">
        <Icon name="pin-solid"
              class="recent-addresses__icon"/>
        <span v-if="address.title"
              class="recent-addresses__address-label__address">
          {{ address.title }}
        </span>
      </span>
      <span class="recent-addresses__address-content">
        {{ address.street }} {{ address.houseNumber }} - {{ address.zipcode }}, {{ address.city }}
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/css/variables/variables.scss";
@import "@/assets/css/mixins/breakpoints-up.scss";

.recent-addresses__address-label__address {
  display: none;

  @include lg-up {
    display: inline;
  }
}

.recent-addresses {
  $self: &;
  margin-top: 1rem;

  &__address {
    cursor:      pointer;
    margin-left: -0.5rem;
    text-align:  center;
    border:      0.5px solid $new-color-neutral-quaternary;
    padding:     0.5rem 1rem;
    width:       max-content;
    max-width:   100%;
    margin:      0 auto 0.5rem;
    display:     flex;
    align-items: center;
    gap:         0.5rem;
    font-size:   1rem;
    border-radius: 10rem;
    transition:  border 0.2s ease, margin-left 0.2s ease, background 0.2s ease;

    &:last-child {
      margin-bottom: 0;
    }

    @include lg-up {
      overflow:   visible;
      border-radius: 1rem;
      gap:        1rem;
      display:    flex;
      padding:    0.5rem 1rem 0.5rem 0.5rem;
      border:     1px solid transparent;
      text-align: left;
      margin:     0 0 0 -0.5rem;
    }

    &:hover {
      @include lg-up {
        margin-left: 0rem;
        border: 1px solid $new-color-neutral-quaternary;

        #{ $self }__address-label {
          border: 1px solid transparent;
          background: $new-gradient-brand-button;
          color:  #ffffff;

          &:deep(path) {
            fill: #ffffff;
          }
        }
      }
    }
  }

  &__address-label {
    display:   flex;
    align-items: center;
    gap:       0.25rem;
    min-width: max-content;

    @include lg-up {
      border:      1px solid $new-color-neutral-quaternary;
      padding:     0.25rem 0.5rem;
      border-radius: 0.5rem;
      font-weight: 700;
    }
  }

  &__address-content {
    max-height: 1.75rem;
    overflow: hidden;
  }

  &__icon {
    min-width: 16px;
    min-height: 27px;
  }
}
</style>
